import { AnimatePresence } from 'framer-motion';
import React, { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Choose from './pages/Choose/Choose';
import Flights from './pages/Flights/Flights';
import Home from './pages/Home/Home';
import Hotels from './pages/Hotels/Hotels';
import Points from './pages/Points/Points';
import Error from './pages/Results/Error';
import Success from './pages/Results/Success';
import SignIn from './pages/Auth/SignIn';
import Welcome from './pages/Welcome/Welcome';
import { store } from './store';
import SignUp from './pages/Auth/SignUp';
import LearnMore from './pages/LearnMore/LearnMore';
import About from './pages/LearnMore/About';
import PasswordCode from './pages/ResetPassword/PasswordCode';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import ConfirmEmail from './pages/Auth/ConfirmEmail';
import useQuery from './hooks/useQuery';
import { useAppDispatch, useAppSelector } from './hooks/useApp';
import SocialAppleSignin from './pages/Auth/Apple';
import { setClientDetails, setTokenFromParent } from './store/slices/authSlice';
import { setPredefinedValues } from './store/slices/checksSlice';

function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = store.getState().auth;
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.token) {
      navigate('/welcome', { replace: true });
    }
    return () => {};
  }, [auth, navigate]);

  return children;
}

const App = () => {
  const location = useLocation();
  const { clientId, source } = useAppSelector((state) => state.auth);

  useEffect(() => {
    analytics.track(`Viewed - ${location.pathname}`, {
      location: window.location.href,
      clientId,
      source
    });
  }, [location]);

  const query = useQuery();
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.addEventListener('message', setToken);
    window.parent.postMessage({ type: 'odynn-widget-ready', value: true }, '*');

    const clientId = query.get('id');
    const source = query.get('source');

    dispatch(setClientDetails({ clientId, source }));

    const people = query.get('people');
    const nights = query.get('nights');
    const destination = query.get('destination');

    const predefinedValues = {
      people: people ? parseInt(people) : null,
      nights: nights ? parseInt(nights) : null,
      class: query.get('class'),
      destination: destination
        ? destination
            .replaceAll('-', ' ')
            .replaceAll('_', '/')
            .split(',')
            .join(', ')
        : null,
      defined: true
    };

    dispatch(setPredefinedValues(predefinedValues));
  }, []);

  const setToken = (event: MessageEvent<any>) => {
    if (event?.data?.type === 'odynn-token') {
      dispatch(setTokenFromParent(event.data.value));
    }
  };

  return (
    <AnimatePresence>
      <Routes location={location} key={location.key}>
        <Route path='/' key={location.key} element={<Home />} />
        <Route path='/signin' key={location.key} element={<SignIn />} />
        <Route path='/signup' key={location.key} element={<SignUp />} />
        <Route path='/welcome' element={<Welcome />} />
        <Route path='/socialApple/:token' element={<SocialAppleSignin />} />
        <Route
          path='/points'
          element={
            <RequireAuth>
              <Points />
            </RequireAuth>
          }
        />
        <Route
          path='/choose'
          element={
            <RequireAuth>
              <Choose />
            </RequireAuth>
          }
        />
        <Route
          path='/flights'
          element={
            <RequireAuth>
              <Flights />
            </RequireAuth>
          }
        />
        <Route
          path='/hotels'
          element={
            <RequireAuth>
              <Hotels />
            </RequireAuth>
          }
        />
        <Route
          path='/success'
          element={
            <RequireAuth>
              <Success />
            </RequireAuth>
          }
        />
        <Route
          path='/error'
          element={
            <RequireAuth>
              <Error />
            </RequireAuth>
          }
        />
        <Route path='/about' element={<About />} />
        <Route path='/learnmore' element={<LearnMore />} />
        <Route path='/resetcode' element={<PasswordCode />} />
        <Route path='/resetpassword' element={<ResetPassword />} />
        <Route path='/confirmemail' element={<ConfirmEmail />} />
      </Routes>
    </AnimatePresence>
  );
};

export default App;
